import html2pdf from 'html2pdf.js';
import { jsPDF } from 'jspdf';
import canvg from 'canvg';

// Base styles for PDF export
const getBasePdfStyles = () => `
  .pdf-export {
    font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;
    color: #000;
    line-height: 1.6;
    padding: 20px;
    max-width: 800px;
    margin: 0 auto;
  }

  .pdf-export h1 {
    font-size: 24px;
    font-weight: bold;
    margin-bottom: 16px;
    color: #000;
    page-break-after: avoid;
    page-break-inside: avoid;
  }

  .pdf-export h2 {
    font-size: 20px;
    font-weight: bold;
    margin-top: 24px;
    margin-bottom: 12px;
    color: #000;
    page-break-after: avoid;
    page-break-inside: avoid;
  }

  .pdf-export h3 {
    font-size: 18px;
    font-weight: bold;
    margin-top: 20px;
    margin-bottom: 10px;
    color: #000;
    page-break-after: avoid;
    page-break-inside: avoid;
  }

  .pdf-export p {
    margin-bottom: 12px;
    color: #000;
  }

  .pdf-export code {
    background-color: #f5f5f5;
    padding: 2px 4px;
    border-radius: 4px;
    font-family: monospace;
  }

  .pdf-export pre {
    background-color: #f5f5f5;
    padding: 12px;
    border-radius: 4px;
    overflow-x: auto;
    margin: 12px 0;
    page-break-inside: avoid;
  }

  .pdf-export ul, .pdf-export ol {
    margin: 12px 0;
    padding-left: 24px;
  }

  .pdf-export li {
    margin-bottom: 6px;
  }

  .pdf-export img {
    max-width: 100%;
    height: auto;
    margin: 16px 0;
    page-break-inside: avoid;
  }

  .pdf-export blockquote {
    border-left: 4px solid #e0e0e0;
    margin: 16px 0;
    padding-left: 16px;
    font-style: italic;
    page-break-inside: avoid;
  }

  .pdf-export table {
    border-collapse: collapse;
    width: 100%;
    margin: 16px 0;
    page-break-inside: avoid;
  }

  .pdf-export th, .pdf-export td {
    border: 1px solid #e0e0e0;
    padding: 8px;
    text-align: left;
  }

  .pdf-export th {
    background-color: #f5f5f5;
  }
`;

// Advanced KaTeX styles for improved equation rendering
const getKatexStyles = (optimizeEquations = true) => `
  .pdf-export .katex { 
    font-size: ${optimizeEquations ? '0.95em' : '1em'};
    display: inline-block !important;
    text-align: left !important;
    padding: ${optimizeEquations ? '0.5em 0' : '0'} !important;
  }
  
  .pdf-export .katex-display { 
    display: block !important;
    margin: ${optimizeEquations ? '2em 0' : '1em 0'} !important;
    text-align: center !important;
    overflow: visible !important;
    position: relative !important;
    padding: ${optimizeEquations ? '0.5em 0' : '0'} !important;
    min-height: ${optimizeEquations ? '3em' : '1.5em'} !important;
    page-break-inside: avoid !important;
  }
  
  .pdf-export .katex-html {
    position: static !important;
    overflow: visible !important;
    display: block !important;
    text-align: center !important;
    white-space: normal !important;
    padding: ${optimizeEquations ? '0.5em 0' : '0'} !important;
  }
  
  .pdf-export .katex-mathml { display: none !important; }
  
  .pdf-export .base { 
    overflow: visible !important;
    padding-top: ${optimizeEquations ? '1em' : '0.5em'} !important;
    padding-bottom: ${optimizeEquations ? '0.5em' : '0.25em'} !important;
  }
  
  .pdf-export .mord { 
    overflow: visible !important;
    padding-top: ${optimizeEquations ? '0.25em' : '0.1em'} !important;
  }
  
  .pdf-export .mrel { overflow: visible !important; }
  
  .pdf-export .msupsub { 
    overflow: visible !important;
    padding-top: ${optimizeEquations ? '0.5em' : '0.25em'} !important;
  }
  
  .pdf-export .vlist-t, .pdf-export .vlist-r, .pdf-export .vlist {
    overflow: visible !important;
  }
  
  .pdf-export .vlist > span {
    overflow: visible !important;
  }
  
  .pdf-export .mfrac {
    padding: ${optimizeEquations ? '0.5em 0' : '0.25em 0'} !important;
    margin: ${optimizeEquations ? '0.25em 0' : '0.15em 0'} !important;
  }

  /* Additional styles for complex math expressions */
  .pdf-export .overline {
    overflow: visible !important;
    padding-top: 0.25em !important;
  }

  .pdf-export .sqrt {
    overflow: visible !important;
    padding-top: 0.25em !important;
  }

  .pdf-export .msubsup {
    overflow: visible !important;
    padding-top: 0.25em !important;
  }

  .pdf-export .stretchy {
    overflow: visible !important;
    display: inline-block !important;
    height: auto !important;
  }

  .pdf-export .strut {
    height: 0.8em !important;
    display: inline-block !important;
  }

  /* Ensure multi-page equations break appropriately */
  .pdf-export .katex-display > .katex {
    padding: 0.5em 0 !important;
    margin: 0.5em 0 !important;
    min-height: 2em !important;
    width: 100% !important;
    display: block !important;
    text-align: center !important;
    overflow: visible !important;
  }

  /* Ensure matrices and arrays have sufficient space */
  .pdf-export .mtable {
    display: inline-table !important;
    margin: 0.5em 0 !important;
    min-height: 3em !important;
    overflow: visible !important;
  }

  .pdf-export .mtable .arraycolsep {
    min-width: 1em !important;
  }

  .pdf-export .mtable .col-align-c {
    text-align: center !important;
  }

  .pdf-export .mtable .col-align-l {
    text-align: left !important;
  }

  .pdf-export .mtable .col-align-r {
    text-align: right !important;
  }
`;

// Page break styles based on user preferences
const getPageBreakStyles = (addPageBreaks = true) => `
  ${addPageBreaks ? `
    /* Core page break rules */
    .pdf-export h1 {
      page-break-before: always;
    }
    
    .pdf-export h1:first-child {
      page-break-before: avoid;
    }
    
    .pdf-export h2, .pdf-export h3 {
      page-break-before: auto;
      page-break-after: avoid;
      page-break-inside: avoid;
    }
    
    /* Avoid breaking these elements across pages */
    .pdf-export table, 
    .pdf-export pre, 
    .pdf-export .katex-display, 
    .pdf-export figure,
    .pdf-export blockquote,
    .pdf-export ul,
    .pdf-export ol,
    .pdf-export .visualization-container {
      page-break-inside: avoid;
    }
    
    /* Keep at least 2 lines together at start/end of paragraphs */
    .pdf-export p {
      orphans: 2;
      widows: 2;
    }
    
    /* Keep captions with their figures */
    .pdf-export figure {
      break-inside: avoid;
    }
    
    /* Keep related headings together */
    .pdf-export h2 + p,
    .pdf-export h3 + p {
      page-break-before: avoid;
    }
    
    /* Keep related lists together */
    .pdf-export p + ul,
    .pdf-export p + ol {
      page-break-before: avoid;
    }
    
    /* Pagination controls for Table of Contents */
    .pdf-export .toc {
      page-break-after: always;
    }
    
    /* Ensure visualizations don't split */
    .pdf-export [id^="visualization"] {
      page-break-inside: avoid;
      display: block;
      margin: 2em 0;
    }
  ` : ''}
`;

// Generate margin settings based on user preference
const getMarginSettings = (marginType) => {
  switch (marginType) {
    case 'narrow':
      return [10, 10, 10, 10]; // Top, right, bottom, left (in mm)
    case 'wide':
      return [25, 25, 25, 25];
    case 'normal':
    default:
      return [15, 15, 15, 15];
  }
};

// Create a shared array to store extracted equations
const extractedEquations = [];

// Enhance the function to handle control systems equations even better
const handleControlSystemsEquations = (content) => {
  // Handle inline variables with dollar signs on both sides - critical for control systems
  content = content.replace(/\$\$([A-Za-z])\$\$/g, '\\(\\mathbf{$1}\\)');
  content = content.replace(/\$([A-Za-z])\$/g, '\\(\\mathbf{$1}\\)');
  
  // Handle other common control systems patterns
  content = content.replace(/\$\$\\alpha = N_p - N_z\$\$/g, '\\(\\alpha = N_p - N_z\\)');
  content = content.replace(/\$\$\\angle G\(s\)H\(s\) = \(2n\+1\)180\^\\circ\$\$/g, 
    '\\(\\angle G(s)H(s) = (2n+1)180^\\circ\\)');
  
  // Handle transfer function patterns
  content = content.replace(/\$\$T\(s\)\s*=\s*\\frac\{K\}\{s\^2\s*\+\s*2s\s*\+\s*K\}\$\$/g, 
    '\\[T(s) = \\frac{K}{s^2 + 2s + K}\\]');
  
  // Handle characteristic equation pattern
  content = content.replace(/\$\$1 \+ KG\(s\)H\(s\) = 0\$\$/g, '\\[1 + KG(s)H(s) = 0\\]');
  
  // Remove empty equation blocks
  content = content.replace(/\$\$\s*\$\$/g, '');
  
  return content;
};

// Use improved patterns in the preprocessing function
const processLatexBeforeMarkdown = (content) => {
  // Pre-process the content for control systems equations
  content = handleControlSystemsEquations(content);
  
  // Replace \( and \) with $ for consistent processing
  content = content.replace(/\\\(/g, '$');
  content = content.replace(/\\\)/g, '$');
  
  // Extract all equations to protect them during markdown conversion
  let processed = content;
  
  // First handle display equations ($$...$$)
  processed = processed.replace(/\$\$([\s\S]*?)\$\$/g, (match, latex) => {
    const id = `display-equation-${extractedEquations.length}`;
    extractedEquations.push({ type: 'display', latex: latex.trim(), id });
    return `<div class="equation-placeholder" data-id="${id}"></div>`;
  });
  
  // Special handling for single variables - they often cause problems
  processed = processed.replace(/\$([A-Za-z])\$/g, (match, variable) => {
    const id = `inline-equation-${extractedEquations.length}`;
    // Use a slightly different format for single variables to ensure they render correctly
    extractedEquations.push({ 
      type: 'inline', 
      latex: `\\mathbf{${variable}}`, // Bold to make single variables stand out
      id, 
      isSingleVar: true 
    });
    return `<span class="equation-placeholder single-var" data-id="${id}"></span>`;
  });
  
  // Then handle other inline equations ($...$) - be careful about money amounts
  processed = processed.replace(/(?<!\$)(?<!\\\$)(?<![0-9])\$([^$\n]+?)\$/g, (match, latex) => {
    // Skip if it looks like a money value
    if (/^\s*\d+(\.\d+)?\s*$/.test(latex)) return match;
    
    const id = `inline-equation-${extractedEquations.length}`;
    extractedEquations.push({ type: 'inline', latex: latex.trim(), id });
    return `<span class="equation-placeholder" data-id="${id}"></span>`;
  });
  
  return processed;
};

// Enhance the prepareContent function
const prepareContent = (content, options) => {
  const {
    includeTableOfContents = true,
    contentSections = {
      title: true,
      headings: true,
      body: true,
      equations: true,
      code: true
    },
    includeVisualizations = true,
    addPageBreaks = true
  } = options;
  
  let processedContent = content;
  
  // Pre-process content to handle control systems specific equation patterns
  processedContent = handleControlSystemsEquations(processedContent);
  
  // First, handle Markdown formatting that might interfere with equations
  
  // Protect any LaTeX equations from markdown processing by replacing them with placeholders
  const equations = [];
  processedContent = processedContent.replace(/(\$\$[\s\S]*?\$\$|\$[^\$\n]+?\$)/g, (match) => {
    const id = `eq-${equations.length}`;
    equations.push(match);
    return `EQUATION_PLACEHOLDER_${id}`;
  });
  
  // Now process markdown formatting
  // Make sure to only replace ** outside of code blocks
  let inCodeBlock = false;
  const lines = processedContent.split('\n').map(line => {
    if (line.trim().startsWith('```')) {
      inCodeBlock = !inCodeBlock;
      return line;
    }
    
    if (!inCodeBlock && line.includes('**')) {
      // Process bold markdown outside code blocks
      return line.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');
    }
    
    return line;
  });
  
  processedContent = lines.join('\n');
  
  // Restore equations
  processedContent = processedContent.replace(/EQUATION_PLACEHOLDER_(eq-\d+)/g, (match, id) => {
    const index = parseInt(id.replace('eq-', ''));
    return equations[index];
  });
  
  // Conditional content inclusion/exclusion
  if (!contentSections.headings) {
    processedContent = processedContent.replace(/#{1,3}.+\n/g, '');
  }
  
  if (!contentSections.code) {
    processedContent = processedContent.replace(/```[\s\S]*?```/g, '');
  }
  
  if (!contentSections.equations) {
    processedContent = processedContent.replace(/\$\$[\s\S]*?\$\$/g, '');
    processedContent = processedContent.replace(/\$[\s\S]*?\$/g, '');
  }
  
  if (!includeVisualizations) {
    processedContent = processedContent.replace(/:::visualization[\s\S]*?:::/g, '');
  }
  
  // Improve equation spacing for better rendering in exports
  processedContent = processedContent.replace(/\$\$([\s\S]*?)\$\$/g, (match, equation) => {
    // Add extra space around display equations for better PDF rendering
    return `\n\n$$${equation.trim()}$$\n\n`;
  });

  // Ensure inline equations have proper spacing
  processedContent = processedContent.replace(/(\S)(\$[^\$\n]+?\$)(\S)/g, '$1 $2 $3');
  
  // Add explicit page break markers if requested
  if (addPageBreaks) {
    // Add page break markers before major sections (h1)
    processedContent = processedContent.replace(/(?<=\n)(#\s)/g, '\n<div class="page-break"></div>\n$1');
    
    // Add markers to prevent page breaks within certain content
    processedContent = processedContent.replace(/(?<=\n)(#{2,3}\s.*\n)/g, '$1<div class="keep-with-next"></div>\n');
  }
  
  return processedContent;
};

// Customizable export function
export const exportToPDF = async (content, title, options = {}, toast) => {
  const {
    pageSize = 'a4',
    orientation = 'portrait',
    margins = 'normal',
    includeVisualizations = true,
    includeTableOfContents = true,
    optimizeEquations = true,
    addPageBreaks = true,
    contentSections = {
      title: true,
      headings: true,
      body: true,
      equations: true,
      code: true
    },
    format = 'pdf'
  } = options;

  // Create toast if provided
  const toastId = toast?.loading ? toast.loading('Preparing export...') : null;

  try {
    // First check if KaTeX is available
    const katexAvailable = typeof window.katex !== 'undefined';
    if (!katexAvailable && contentSections.equations) {
      console.warn('KaTeX not available but equations are requested. Attempting to load KaTeX dynamically.');
      
      // Try to dynamically load KaTeX if it's not available and equations are needed
      if (document.querySelector('link[href*="katex.min.css"]') === null) {
        const link = document.createElement('link');
        link.rel = 'stylesheet';
        link.href = 'https://cdn.jsdelivr.net/npm/katex@0.16.8/dist/katex.min.css';
        document.head.appendChild(link);
      }
      
      if (typeof window.katex === 'undefined') {
        // Load KaTeX script dynamically
        await new Promise((resolve, reject) => {
          const script = document.createElement('script');
          script.src = 'https://cdn.jsdelivr.net/npm/katex@0.16.8/dist/katex.min.js';
          script.onload = resolve;
          script.onerror = reject;
          document.head.appendChild(script);
        }).catch(err => {
          console.error('Failed to load KaTeX dynamically:', err);
        });
      }
    }

    // Create container for export
    const exportContainer = document.createElement('div');
    exportContainer.className = 'pdf-export';
    document.body.appendChild(exportContainer);

    // Build combined styles
    const combinedStyles = `
      ${getBasePdfStyles()}
      ${getKatexStyles(optimizeEquations)}
      ${getPageBreakStyles(addPageBreaks)}
      
      /* Additional styles for page break control */
      .pdf-export .page-break {
        page-break-before: always;
        display: block;
        height: 0;
        clear: both;
      }
      
      .pdf-export .keep-with-next {
        page-break-after: avoid;
        display: block;
      }
      
      /* Fix for excessive whitespace around equations */
      .pdf-export .equation-container {
        page-break-inside: avoid;
        display: block;
        margin: 0.15em 0; /* Reduced from 0.3em */
        padding: 0;
        line-height: 1.2; /* Reduced from 1.3 */
      }
      
      .pdf-export .katex-display {
        margin: 0.15em 0 !important; /* Reduced from 0.3em */
        padding: 0 !important;
        overflow: visible !important;
        line-height: 1.2 !important; /* Reduced from 1.3 */
      }
      
      .pdf-export .katex {
        text-align: center !important;
        display: inline-block !important;
        padding: 0 !important;
        margin: 0 !important;
        line-height: 1 !important; /* Reduced from 1.1 */
      }
      
      .pdf-export .katex-html {
        padding: 0 !important;
        margin: 0 !important;
        line-height: 1 !important; /* Reduced from 1.1 */
      }
      
      /* Special handling for single variable equations */
      .pdf-export .single-var .katex {
        margin: 0 0.05em !important; /* Reduced from 0.1em */
        padding: 0 !important;
        display: inline !important;
      }
      
      /* Fix for stretchable symbols */
      .pdf-export .katex .stretchy {
        margin: 0 !important;
        padding: 0 !important;
      }
      
      /* Fix for vertical spacing in fractions */
      .pdf-export .katex .mfrac {
        margin: 0 !important;
        padding: 0 !important;
      }
      
      /* Reduce the space between lines in paragraphs containing equations */
      .pdf-export p:has(.katex) {
        margin: 0.4em 0 !important; /* Reduced from 0.5em */
        line-height: 1.2 !important; /* Reduced from 1.3 */
      }
    `;

    // Add styles to document
    const styleElement = document.createElement('style');
    styleElement.setAttribute('data-pdf-styles', 'true');
    styleElement.textContent = combinedStyles;
    document.head.appendChild(styleElement);

    // Populate container with content
    let contentHtml = '';
    
    if (contentSections.title) {
      contentHtml += `
        <h1 style="font-size: 20px; font-weight: bold; margin-bottom: 20px; color: black;">
          ${title}
        </h1>
      `;
    }
    
    if (includeTableOfContents && content.match(/#{1,3}.+/g)) {
      const headings = content.match(/#{1,3}.+/g) || [];
      if (headings.length > 0) {
        contentHtml += `<div class="toc" style="margin-bottom: 30px; page-break-after: always;">
          <h2>Table of Contents</h2>
          <ul style="list-style-type: none; padding-left: 0;">`;
        headings.forEach(heading => {
          const headingText = heading.replace(/#/g, '').trim();
          const headingLevel = (heading.match(/#/g) || []).length;
          const padding = (headingLevel - 1) * 15;
          contentHtml += `<li style="padding-left: ${padding}px; margin-bottom: 8px;">${headingText}</li>`;
        });
        contentHtml += `</ul></div>`;
      }
    }
    
    // Use the enhanced content preparation
    const processedContent = prepareContent(content, options);

    // Pre-process to separate LaTeX from markdown
    const htmlWithPlaceholders = processLatexBeforeMarkdown(processedContent);
    
    // Convert markdown to HTML
    let htmlContent = '';
    const markdownIt = window.markdownit ? window.markdownit() : null;
    
    if (markdownIt) {
      htmlContent = markdownIt.render(htmlWithPlaceholders);
    } else {
      // Fallback basic markdown conversion
      htmlContent = htmlWithPlaceholders
        .replace(/^### (.*$)/gm, '<h3>$1</h3>')
        .replace(/^## (.*$)/gm, '<h2>$1</h2>')
        .replace(/^# (.*$)/gm, '<h1>$1</h1>')
        .replace(/^(\s*)((?!<div|<span|<h[1-6]|<p|<a).+)$/gm, '<p>$2</p>');
    }
    
    // Restore equations with proper KaTeX markup
    const restoreEquations = (html) => {
      let processed = html;
      
      // Replace all equation placeholders with their corresponding LaTeX
      extractedEquations.forEach(eq => {
        const placeholder = eq.type === 'display' 
          ? `<div class="equation-placeholder" data-id="${eq.id}"></div>` 
          : `<span class="equation-placeholder${eq.isSingleVar ? ' single-var' : ''}" data-id="${eq.id}"></span>`;
        
        let replacementEl;
        if (eq.type === 'display') {
          // For display equations, use div with proper classes
          replacementEl = `<div class="equation-container"><div class="katex-display"><span class="katex-latex" data-latex="${eq.latex}">$$${eq.latex}$$</span></div></div>`;
        } else if (eq.isSingleVar) {
          // For single variables, use special handling
          replacementEl = `<span class="katex-inline single-var" data-latex="${eq.latex}" data-single-var="true">\\(${eq.latex}\\)</span>`;
        } else {
          // For normal inline equations
          replacementEl = `<span class="katex-inline" data-latex="${eq.latex}">\\(${eq.latex}\\)</span>`;
        }
        
        processed = processed.replace(new RegExp(placeholder, 'g'), replacementEl);
      });
      
      return processed;
    };
    
    htmlContent = restoreEquations(htmlContent);
    contentHtml += htmlContent;
    exportContainer.innerHTML = contentHtml;
    
    // Render all LaTeX equations with KaTeX
    if (window.katex) {
      if (toast?.loading) {
        toast.loading('Rendering equations...', { id: toastId });
      }
      
      // Helper function to render an equation element
      const renderLatexElement = (element) => {
        try {
          if (element.classList.contains('katex-rendered')) {
            return; // Already rendered
          }
          
          let latex = element.getAttribute('data-latex');
          const isSingleVar = element.getAttribute('data-single-var') === 'true';
          
          if (!latex) {
            // Try to extract from content if no data-latex
            const content = element.textContent.trim();
            if (content.startsWith('$$') && content.endsWith('$$')) {
              latex = content.substring(2, content.length - 2).trim();
              element.setAttribute('data-latex', latex);
            } else if ((content.startsWith('$') && content.endsWith('$')) || 
                      (content.startsWith('\\(') && content.endsWith('\\)'))) {
              // Extract the latex from either $ or \( delimiters
              latex = content.replace(/^\\\(|\\\)$|^\$|\$$/g, '').trim();
              element.setAttribute('data-latex', latex);
            }
          }
          
          if (!latex) {
            console.warn('No LaTeX content found in element:', element);
            return;
          }
          
          const isDisplay = element.classList.contains('katex-display') || 
                          element.classList.contains('katex-latex');
          
          // Special handling for single variables
          if (isSingleVar || /^[A-Za-z]$/.test(latex)) {
            // For single variables, wrap in mathbf and ensure they have proper context
            if (!latex.includes('\\mathbf') && !latex.startsWith('{') && !latex.endsWith('}')) {
              latex = `\\mathbf{${latex}}`;
            }
            
            window.katex.render(latex, element, {
              displayMode: false,
              throwOnError: false,
              output: 'html',
              trust: true
            });
          } else {
            window.katex.render(latex, element, {
              displayMode: isDisplay,
              throwOnError: false,
              output: 'html',
              trust: true // This helps with more complex equations
            });
          }
          
          element.classList.add('katex-rendered');
          
          // Add a more compact wrapper for single variables
          if (element.classList.contains('single-var') && !element.parentElement.classList.contains('var-wrapper')) {
            const wrapper = document.createElement('span');
            wrapper.className = 'var-wrapper';
            wrapper.style.display = 'inline-block';
            wrapper.style.margin = '0 0.05em'; // Reduced spacing
            element.parentNode.insertBefore(wrapper, element);
            wrapper.appendChild(element);
          }
        } catch (error) {
          console.error('Error rendering equation:', error, 'LaTeX:', element.getAttribute('data-latex'));
          // Fallback for rendering errors - show the raw LaTeX in black text
          if (!element.querySelector('.katex-fallback')) {
            const fallback = document.createElement('span');
            fallback.className = 'katex-fallback';
            fallback.style.fontFamily = 'monospace';
            fallback.style.color = 'black'; // Make fallback text black instead of red
            fallback.textContent = element.getAttribute('data-latex') || element.textContent.replace(/^\$\$|\$\$$|^\$|\$$/g, '');
            element.innerHTML = '';
            element.appendChild(fallback);
          }
        }
      };
      
      // First pass - render all elements with the katex-latex or katex-inline class
      const displayEquations = exportContainer.querySelectorAll('.katex-latex');
      const inlineEquations = exportContainer.querySelectorAll('.katex-inline');
      
      displayEquations.forEach(renderLatexElement);
      inlineEquations.forEach(renderLatexElement);
      
      // Second pass - find any remaining LaTeX in text nodes
      const processTextNodesForLatex = (node) => {
        if (node.nodeType === Node.TEXT_NODE) {
          const text = node.textContent;
          if (text.includes('$$') || text.includes('$')) {
            const temp = document.createElement('div');
            
            // Replace display equations
            let processedText = text.replace(/\$\$([\s\S]*?)\$\$/g, (match, latex) => {
              try {
                const wrapper = document.createElement('div');
                wrapper.className = 'equation-container';
                const inner = document.createElement('div');
                inner.className = 'katex-display';
                inner.setAttribute('data-latex', latex.trim());
                wrapper.appendChild(inner);
                
                window.katex.render(latex.trim(), inner, {
                  displayMode: true,
                  throwOnError: false
                });
                
                return wrapper.outerHTML;
              } catch (e) {
                console.error('Error processing display equation:', e);
                return match;
              }
            });
            
            // Replace inline equations - but only if they don't look like money
            processedText = processedText.replace(/(?<!\$)(?<![0-9])\$([^$\n]+?)\$/g, (match, latex) => {
              // Skip if it looks like a money amount
              if (/^\s*\d+(\.\d+)?\s*$/.test(latex)) return match;
              
              try {
                const span = document.createElement('span');
                span.className = 'katex-inline';
                
                window.katex.render(latex.trim(), span, {
                  displayMode: false,
                  throwOnError: false
                });
                
                return span.outerHTML;
              } catch (e) {
                console.error('Error processing inline equation:', e);
                return match;
              }
            });
            
            if (processedText !== text) {
              temp.innerHTML = processedText;
              const parent = node.parentNode;
              const fragment = document.createDocumentFragment();
              
              while (temp.firstChild) {
                fragment.appendChild(temp.firstChild);
              }
              
              parent.replaceChild(fragment, node);
            }
          }
        } else if (node.nodeType === Node.ELEMENT_NODE) {
          // Skip if it's already a KaTeX element or contains KaTeX
          if (node.classList.contains('katex') || 
              node.closest('.katex') || 
              node.classList.contains('katex-rendered')) {
            return;
          }
          
          // Process child nodes
          const childNodes = Array.from(node.childNodes);
          childNodes.forEach(processTextNodesForLatex);
        }
      };
      
      // Process text nodes in paragraphs and other content containers
      exportContainer.querySelectorAll('p, li, h1, h2, h3, h4, h5, h6, div').forEach(el => {
        if (!el.querySelector('.katex')) {
          processTextNodesForLatex(el);
        }
      });
      
      // Allow time for rendering to complete
      await new Promise(resolve => setTimeout(resolve, 2000));
    } else {
      console.warn('KaTeX not available for equation rendering');
    }
    
    // Apply additional styles to rendered content
    exportContainer.querySelectorAll('p').forEach(el => {
      el.style.cssText += 'margin: 0.8em 0; color: #000;';
    });
    
    exportContainer.querySelectorAll('h1, h2, h3, h4, h5, h6').forEach(el => {
      el.style.cssText += 'color: #000; page-break-after: avoid;';
    });
    
    // Wait a moment for everything to settle
    if (toast?.loading) {
      toast.loading('Finalizing document...', { id: toastId });
    }
    await new Promise(resolve => setTimeout(resolve, 1000));

    // Handle different export formats
    if (format === 'html') {
      // Create a blob and download link for HTML export
      const htmlDocument = `
        <!DOCTYPE html>
        <html>
        <head>
          <meta charset="UTF-8">
          <title>${title}</title>
          <style>${combinedStyles}</style>
          <link rel="stylesheet" href="https://cdn.jsdelivr.net/npm/katex@0.16.8/dist/katex.min.css">
          <script defer src="https://cdn.jsdelivr.net/npm/katex@0.16.8/dist/katex.min.js"></script>
          <script>
            // Automatically render any unprocessed equations when the document loads
            document.addEventListener("DOMContentLoaded", function() {
              // Wait for KaTeX to load
              function waitForKaTeX() {
                if (typeof katex !== 'undefined') {
                  renderAllEquations();
                } else {
                  setTimeout(waitForKaTeX, 100);
                }
              }
              
              function renderAllEquations() {
                // Render all elements with data-latex attribute
                document.querySelectorAll('[data-latex]:not(.katex-rendered)').forEach(el => {
                  const latex = el.getAttribute('data-latex');
                  const displayMode = el.classList.contains('katex-display') || 
                                     el.classList.contains('katex-latex') ||
                                     el.parentElement?.classList.contains('katex-display');
                  
                  try {
                    katex.render(latex, el, { 
                      displayMode: displayMode,
                      throwOnError: false,
                      output: 'html'
                    });
                    el.classList.add('katex-rendered');
                  } catch (e) {
                    console.error('Error rendering equation:', e);
                  }
                });
                
                // Look for any $ or $$ that might have been missed
                document.querySelectorAll('p, li, div:not(.katex)').forEach(el => {
                  if (el.innerHTML.includes('$$') || el.innerHTML.includes('$')) {
                    let newHTML = el.innerHTML;
                    
                    // Replace display equations
                    newHTML = newHTML.replace(/\$\$([\s\S]*?)\$\$/g, (match, latex) => {
                      try {
                        const container = document.createElement('div');
                        container.className = 'equation-container';
                        const display = document.createElement('div');
                        display.className = 'katex-display';
                        container.appendChild(display);
                        
                        katex.render(latex.trim(), display, {
                          displayMode: true,
                          throwOnError: false
                        });
                        
                        return container.outerHTML;
                      } catch (e) {
                        console.error('Error processing display equation:', e);
                        return match;
                      }
                    });
                    
                    // Replace inline equations
                    newHTML = newHTML.replace(/(?<!\$|[0-9])\$([^$\n]+?)\$/g, (match, latex) => {
                      // Skip if it looks like a money amount
                      if (/^\s*\d+(\.\d+)?\s*$/.test(latex)) return match;
                      
                      try {
                        const span = document.createElement('span');
                        span.className = 'katex-inline';
                        
                        katex.render(latex.trim(), span, {
                          displayMode: false,
                          throwOnError: false
                        });
                        
                        return span.outerHTML;
                      } catch (e) {
                        console.error('Error processing inline equation:', e);
                        return match;
                      }
                    });
                    
                    if (newHTML !== el.innerHTML) {
                      el.innerHTML = newHTML;
                    }
                  }
                });
              }
              
              // Start the process
              waitForKaTeX();
            });
          </script>
        </head>
        <body>
          ${exportContainer.outerHTML}
        </body>
        </html>
      `;
      
      const blob = new Blob([htmlDocument], { type: 'text/html' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${title.replace(/[^a-z0-9]/gi, '_').toLowerCase()}_notes.html`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      
      if (toast?.success) {
        toast.success('HTML document exported successfully', { id: toastId });
      }
    } else {
      // PDF export configuration
      if (toast?.loading) {
        toast.loading('Generating PDF...', { id: toastId });
      }
      
      const marginSettings = getMarginSettings(margins);
      
      const opt = {
        margin: marginSettings,
        filename: `${title.replace(/[^a-z0-9]/gi, '_').toLowerCase()}_notes.pdf`,
        image: { type: 'jpeg', quality: 0.98 },
        html2canvas: {
          scale: 2,
          useCORS: true,
          letterRendering: true,
          scrollY: 0,
          width: orientation === 'portrait' ? 794 : 1123, // A4 width in pixels at 96 DPI
          windowWidth: orientation === 'portrait' ? 794 : 1123,
          height: undefined, // Let it calculate height automatically
          onclone: function (clonedDoc) {
            // Force render any remaining equations in the cloned document
            if (window.katex) {
              clonedDoc.querySelectorAll('[data-latex]:not(.katex-rendered)').forEach(el => {
                const latex = el.getAttribute('data-latex');
                if (latex) {
                  const displayMode = el.classList.contains('katex-display') || 
                                     el.classList.contains('katex-latex') ||
                                     el.parentElement?.classList.contains('katex-display');
                  
                  try {
                    window.katex.render(latex, el, {
                      displayMode: displayMode,
                      throwOnError: false
                    });
                    el.classList.add('katex-rendered');
                  } catch (error) {
                    console.error('Error rendering equation in clone:', error);
                  }
                }
              });
              
              // Look one more time for any $ or $$ in text nodes
              const processNode = (node) => {
                if (node.nodeType === Node.TEXT_NODE) {
                  const text = node.textContent;
                  if (text && (text.includes('$$') || text.includes('$'))) {
                    const span = document.createElement('span');
                    
                    // First process display equations
                    let processedText = text.replace(/\$\$([\s\S]*?)\$\$/g, (match, latex) => {
                      try {
                        const wrapper = document.createElement('div');
                        wrapper.className = 'equation-container';
                        const inner = document.createElement('div');
                        inner.className = 'katex-display';
                        wrapper.appendChild(inner);
                        
                        window.katex.render(latex.trim(), inner, {
                          displayMode: true,
                          throwOnError: false
                        });
                        
                        return wrapper.outerHTML;
                      } catch (error) {
                        console.error('Error rendering display equation in clone text:', error);
                        return match;
                      }
                    });
                    
                    // Then process inline equations
                    processedText = processedText.replace(/(?<!\$|[0-9])\$([^$\n]+?)\$/g, (match, latex) => {
                      // Skip if it looks like a money amount
                      if (/^\s*\d+(\.\d+)?\s*$/.test(latex)) return match;
                      
                      try {
                        const inline = document.createElement('span');
                        inline.className = 'katex-inline';
                        
                        window.katex.render(latex.trim(), inline, {
                          displayMode: false,
                          throwOnError: false
                        });
                        
                        return inline.outerHTML;
                      } catch (error) {
                        console.error('Error rendering inline equation in clone text:', error);
                        return match;
                      }
                    });
                    
                    if (processedText !== text) {
                      span.innerHTML = processedText;
                      node.parentNode.replaceChild(span, node);
                    }
                  }
                } else if (node.nodeType === Node.ELEMENT_NODE) {
                  // Skip KaTeX elements
                  if (node.classList.contains('katex') || 
                      node.closest('.katex') ||
                      node.classList.contains('katex-rendered')) {
                    return;
                  }
                  
                  // Process child nodes
                  Array.from(node.childNodes).forEach(processNode);
                }
              };
              
              clonedDoc.querySelectorAll('p, li, h1, h2, h3, h4, h5, h6, div').forEach(el => {
                if (!el.querySelector('.katex') && !el.classList.contains('katex-rendered')) {
                  Array.from(el.childNodes).forEach(processNode);
                }
              });
            }
            
            // Make sure all text elements have the right color
            clonedDoc.querySelectorAll('p, li, h1, h2, h3, h4, h5, h6, span:not(.katex *)').forEach(el => {
              if (!el.style.color) {
                el.style.color = '#000';
              }
            });
          }
        },
        jsPDF: {
          unit: 'mm',
          format: pageSize,
          orientation: orientation,
          compress: true,
          // These settings help with text selectability
          hotfixes: ['px_scaling'],
          textRendering: 'legibility'
        },
        // This is critical for selectable text
        enableLinks: true,
        // Enable special font handling
        fontFaces: true
      };

      // Generate PDF
      await html2pdf().set(opt).from(exportContainer).save();
      
      if (toast?.success) {
        toast.success('PDF exported successfully', { id: toastId });
      }
    }
    
    return true;
  } catch (error) {
    console.error('Error in export process:', error);
    
    if (toast?.error) {
      toast.error(`Failed to export: ${error.message}`, { id: toastId });
    }
    
    return false;
  } finally {
    // Clean up
    const exportContainer = document.querySelector('.pdf-export');
    if (exportContainer) {
      document.body.removeChild(exportContainer);
    }
    
    const styleEl = document.querySelector('style[data-pdf-styles]');
    if (styleEl) {
      document.head.removeChild(styleEl);
    }
  }
};

export default exportToPDF; 